import { Culture } from "../models/culture";
import { IFormatterService, INumberFormatter, IDateFormatter, ToStringResult } from "../models/formatter";
import { NumberFormatterService } from "./number-formatter.service";

const DATE_OBJECT = "[object Date]";
const FORMAT_REGEX = /\{(\d+)(:[^\}]+)?\}/g;
const objectToString = {}.toString;

export class FormatterService implements IFormatterService {
    constructor(
        private numberFormatter: INumberFormatter,
        private dateFormatter: IDateFormatter
    ) {}

    /**
     * Format a value to string, or return it as-is if no format specified.
     * The loose return type matches original kendo.toString behavior where
     * unformatted values pass through (e.g., null stays null for jQuery .val())
     */
    toString(value: Date | number | null | undefined, format?: string, culture?: string | Culture): ToStringResult {
        if (format) {
            if (objectToString.call(value) === DATE_OBJECT) {
                return this.dateFormatter.format(value as Date, format, culture);
            } else if (typeof value === "number") {
                return this.numberFormatter.format(value, format, culture);
            }
        }

        // Match original behavior: return value as-is (not String(value))
        // This allows jQuery .val(null) to work correctly (clears input)
        return value !== undefined ? value : "";
    }

    format(fmt: string, ...values: unknown[]): string {
        return fmt.replace(FORMAT_REGEX, (_, index, placeholderFormat) => {
            const value = values[parseInt(index, 10)];
            const result = this.toString(
                value as Date | number | undefined, 
                placeholderFormat ? placeholderFormat.substring(1) : ""
            );
            // Let JS coerce to string (null becomes "null", undefined becomes "undefined")
            // This matches original behavior where String.replace does implicit coercion
            return String(result);
        });
    }

    extractFormat(format: string): string {
        if (format.slice(0, 3) === "{0:") {
            format = format.slice(3, format.length - 1);
        }
        return format;
    }

    /**
     * Get the number formatter instance (for direct access)
     */
    getNumberFormatter(): INumberFormatter {
        return this.numberFormatter;
    }

    /**
     * Get the date formatter instance (for direct access)
     */
    getDateFormatter(): IDateFormatter {
        return this.dateFormatter;
    }

    /**
     * Static round method exposed for external use (matches kendo._round)
     */
    static round(value: number, precision: number, negative?: boolean): string {
        return NumberFormatterService.round(value, precision, negative);
    }
}
